/**
 * TrueFalseQuestion Component
 * True/False question type - binary choice
 * Supports two modes:
 *   1. Options provided (question.options.length > 0) — renders option text (multi-language), sends option ID
 *   2. No options — renders hardcoded True/False, sends boolean
 */

import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { QuestionLearner } from '@/types/assessment.types';

export interface TrueFalseQuestionProps {
  question: QuestionLearner;
  selectedAnswer?: boolean | string; // boolean when no options, option ID string when options exist
  onSelect: (answer: boolean | string) => void;
  disabled?: boolean;
  showCorrect?: boolean;
  correctAnswer?: boolean; // For results view (no-options fallback)
}

export const TrueFalseQuestion: React.FC<TrueFalseQuestionProps> = ({
  question,
  selectedAnswer,
  onSelect,
  disabled = false,
  showCorrect = false,
  correctAnswer,
}) => {
  const hasOptions = question.options && question.options.length > 0;

  // ── Options-based rendering (multi-language support) ──
  if (hasOptions) {
    const getOptionStyle = (optionId: string) => {
      const isSelected = selectedAnswer === optionId;

      if (isSelected) {
        return 'border-primary-500 bg-primary-50';
      }
      return 'border-gray-200 hover:border-gray-300 hover:bg-gray-50';
    };

    return (
      <div className="space-y-3">
        {/* Question Text */}
        <div className="flex items-start gap-2 mb-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{question.question_text}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
              <span className="px-2 py-0.5 bg-gray-100 rounded">{question.points} points</span>
            </div>
          </div>
        </div>

        {/* Options from question.options */}
        <div className="grid grid-cols-2 gap-3">
          {question.options!.map(option => (
            <button
              key={option.id}
              onClick={() => !disabled && onSelect(option.id)}
              disabled={disabled}
              className={`p-4 border rounded-lg transition-all ${getOptionStyle(option.id)} ${
                disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                {/* Radio Button */}
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedAnswer === option.id
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-gray-300'
                  }`}
                >
                  {selectedAnswer === option.id && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>

                {/* Label — uses option text (supports any language) */}
                <span className="text-base font-medium text-gray-900">{option.text}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Fallback: no options — hardcoded True / False ──
  const getOptionStyle = (value: boolean) => {
    const isSelected = selectedAnswer === value;
    const isCorrect = showCorrect && correctAnswer === value;
    const isIncorrect = showCorrect && isSelected && correctAnswer !== value;

    if (isIncorrect) {
      return 'border-red-300 bg-red-50';
    }
    if (isCorrect) {
      return 'border-green-300 bg-green-50';
    }
    if (isSelected) {
      return 'border-primary-500 bg-primary-50';
    }
    return 'border-gray-200 hover:border-gray-300 hover:bg-gray-50';
  };

  return (
    <div className="space-y-3">
      {/* Question Text */}
      <div className="flex items-start gap-2 mb-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{question.question_text}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="px-2 py-0.5 bg-gray-100 rounded">{question.points} points</span>
          </div>
        </div>
      </div>

      {/* True/False Options */}
      <div className="grid grid-cols-2 gap-3">
        {/* True Button */}
        <button
          onClick={() => !disabled && onSelect(true)}
          disabled={disabled}
          className={`p-4 border rounded-lg transition-all ${getOptionStyle(true)} ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className="flex flex-col items-center gap-2">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedAnswer === true
                  ? showCorrect && correctAnswer === false
                    ? 'border-red-500 bg-red-500'
                    : 'border-primary-500 bg-primary-500'
                  : 'border-gray-300'
              }`}
            >
              {selectedAnswer === true && <div className="w-2 h-2 bg-white rounded-full" />}
            </div>
            <span className="text-base font-medium text-gray-900">True</span>
            {showCorrect && correctAnswer === true && (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
            {showCorrect && selectedAnswer === true && correctAnswer === false && (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
          </div>
        </button>

        {/* False Button */}
        <button
          onClick={() => !disabled && onSelect(false)}
          disabled={disabled}
          className={`p-4 border rounded-lg transition-all ${getOptionStyle(false)} ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className="flex flex-col items-center gap-2">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedAnswer === false
                  ? showCorrect && correctAnswer === true
                    ? 'border-red-500 bg-red-500'
                    : 'border-primary-500 bg-primary-500'
                  : 'border-gray-300'
              }`}
            >
              {selectedAnswer === false && <div className="w-2 h-2 bg-white rounded-full" />}
            </div>
            <span className="text-base font-medium text-gray-900">False</span>
            {showCorrect && correctAnswer === false && (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
            {showCorrect && selectedAnswer === false && correctAnswer === true && (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
          </div>
        </button>
      </div>
    </div>
  );
};
